/**
 * useArchLayout — misst die verfügbare Inhaltsbreite und entscheidet daraus
 * zwischen Nebeneinander (`wide`) und Untereinander (`narrow`).
 *
 * Warum gemessen und nicht `@media` oder `@container`:
 *
 *  1. **Eine Quelle.** Der Umbruch schaltet nicht nur Layout, sondern auch das
 *     Kanten-Overlay ab und ändert den sichtbaren Kantenzähler. Eine Media-Query
 *     kann das nicht — es gäbe zwei Schwellen, die auseinanderlaufen können.
 *     Hier setzt JS `data-layout` am Wurzelelement, CSS folgt dem Attribut.
 *  2. **Die Fensterbreite ist die falsche Größe.** Zwischen Fenster und Bahnen
 *     liegen Sidebar (248 px), Shell-Polster und der `max-width: 1280px`-Deckel
 *     von `.page`. Gemessen wird deshalb das Element, in dem das Grid wirklich
 *     steht.
 *
 * Ohne `ResizeObserver` (jsdom, ältere Umgebungen) fällt der Hook auf das
 * `resize`-Ereignis zurück; misst er 0 px (kein Layout), bleibt es bei `wide`.
 */
import { useEffect, useLayoutEffect, useState, type RefObject } from "react";
import { archLayout, type ArchLayout } from "./archModel";

export function useArchLayout(ref: RefObject<HTMLElement | null>): ArchLayout {
  const [layout, setLayout] = useState<ArchLayout>("wide");

  // Vor dem Paint messen: sonst stünde ein Frame lang das breite Grid da und
  // die Seite ruckte beim ersten Anzeigen sichtbar um.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setLayout(archLayout(el.getBoundingClientRect().width));
    read();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", read);
      return () => window.removeEventListener("resize", read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  // Beim ERSTEN Commit ist die `ref` des Elternelements im Layout-Effekt des
  // Kindes noch nicht gesetzt; dieselbe Nachmessung wie in `useFlowEdges`.
  useEffect(() => {
    const el = ref.current;
    if (el) setLayout(archLayout(el.getBoundingClientRect().width));
  }, [ref]);

  return layout;
}
